import React, { useState, useRef, useEffect } from 'react';
import { adminFetch } from '@/utils/adminAuth';
import { toast } from 'sonner';

/**
 * Inline-editable price for a sample product.
 * Admins can click the price to edit it; non-admins see a static span.
 */
export const EditablePrice = ({
  productId,
  field = 'price',          // 'price' or 'sale_price'
  value,
  canEdit = false,
  className = '',
  prefix = '$',
  onUpdated,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setDraft(value ?? '');
  }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const save = async () => {
    const num = parseFloat(draft);
    if (Number.isNaN(num) || num < 0) {
      toast.error('Enter a valid price');
      setDraft(value ?? '');
      setEditing(false);
      return;
    }
    if (num === Number(value)) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const res = await adminFetch(`/sample-store/products/${productId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: num }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      toast.success('Price updated');
      onUpdated?.(field, num);
      setEditing(false);
    } catch (err) {
      toast.error(err.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); save(); }
    else if (e.key === 'Escape') { setDraft(value ?? ''); setEditing(false); }
  };

  if (!canEdit) {
    return <span className={className}>{prefix}{value}</span>;
  }

  if (editing) {
    return (
      <span className={`inline-flex items-center ${className}`}>
        <span>{prefix}</span>
        <input
          ref={inputRef}
          type="number"
          step="0.01"
          min="0"
          disabled={saving}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={save}
          onKeyDown={onKeyDown}
          className="bg-transparent border-b border-amber-500 w-20 outline-none text-inherit font-inherit"
          data-testid={`price-input-${productId}-${field}`}
        />
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      className={`${className} outline-dashed outline-2 outline-yellow-400/70 outline-offset-2 rounded-sm cursor-pointer text-left`}
      title="Click to edit price"
      data-testid={`price-edit-${productId}-${field}`}
    >
      {prefix}{value}
    </button>
  );
};
